import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { 
  Compass, BarChart3, Sparkles, CheckCircle, ChevronDown, ChevronUp,
  ArrowRight, Play, Target, Lightbulb, Palette, Zap, Calendar,
  Users, Star, Clock, Shield, CreditCard
} from 'lucide-react';

// Hero image from provided assets
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5fd3248c-5412-4532-920b-e0e60b4481dd/images/390d36dfc7b5028b207f7d0e820eec5bfecab6f2f1bb0996aa680ca63b88d949.jpeg";
const TRANSFORMATION_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5fd3248c-5412-4532-920b-e0e60b4481dd/images/18ec952f30179f41eaf7c7b7abc4f216a9fc02a37b14a9bcf1734d12cd3250e4.jpeg";
const FINAL_CTA_IMAGE = "https://static.prod-images.emergentagent.com/jobs/5fd3248c-5412-4532-920b-e0e60b4481dd/images/252317f5c314ef2e5828f2aeaf84663209aa48a407781995bcf110e1b2b70c98.jpeg";

const Join = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isFoundingVisionary, setIsFoundingVisionary] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(true);

  // Hide sticky CTA when footer/final CTA is visible
  useEffect(() => {
    const handleScroll = () => {
      const footer = document.getElementById('join-footer');
      const finalCta = document.getElementById('final-cta-section');
      
      if (footer && finalCta) {
        const footerRect = footer.getBoundingClientRect();
        const finalCtaRect = finalCta.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Hide sticky when final CTA or footer enters viewport
        if (finalCtaRect.top < windowHeight || footerRect.top < windowHeight) {
          setShowStickyCta(false);
        } else {
          setShowStickyCta(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('visionlab_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get(`${API}/subscriptions/plans`, { headers });
      setPlans(response.data.plans);
      setIsFoundingVisionary(response.data.is_founding_visionary || false);
    } catch (error) {
      console.error('Error fetching plans:', error);
      // Fallback to default plans
      setPlans([
        {
          plan_id: 'essentiel',
          name: 'VisionLab Essential',
          tagline: 'Ton premier pas vers la clarté',
          price_monthly: 29.00,
          price_annual: 290.00,
          currency: 'CHF',
          features: [
            "Audit de marque IA personnalisé",
            "Générateur d'identité visuelle",
            "5 idées de contenu par mois",
            "Roadmap visuelle 30 jours",
            "Stratégie de contenu guidée",
            "Accès communauté VisionLab"
          ]
        },
        {
          plan_id: 'experience',
          name: 'VisionLab Experience',
          tagline: "L'expérience complète pour les entrepreneures ambitieuses",
          price_monthly: 69.00,
          price_annual: 690.00,
          currency: 'CHF',
          features: [
            "Tout VisionLab Essential inclus",
            "Contenu illimité chaque mois",
            "Content Studio complet",
            "Roadmap stratégique trimestrielle",
            "Annuaire Business & Women Connect",
            "Support prioritaire",
            "Accès anticipé aux nouveautés"
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCTA = () => {
    if (user) {
      navigate('/pricing');
    } else {
      navigate('/auth');
    }
  };

  const handleSelectPlan = (planId) => {
    navigate(`/pricing?plan=${planId}&billing=${billingCycle}`);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // FAQ Data
  const faqItems = [
    {
      question: "Qu'est-ce que VisionLab ?",
      answer: "VisionLab est ton business companion qui t'aide à transformer ta vision en actions concrètes. C'est un espace où tu peux clarifier ton identité de marque, structurer ta stratégie et créer du contenu aligné avec ta vision."
    },
    {
      question: "À qui s'adresse VisionLab ?",
      answer: "VisionLab est conçu pour les femmes entrepreneures et porteuses de vision qui souhaitent développer leur activité avec davantage de clarté, de structure et d'intention. Que tu lances ton projet ou que tu cherches à le faire évoluer."
    },
    {
      question: "VisionLab convient-il si je débute ?",
      answer: "Absolument. VisionLab t'accompagne à chaque étape, que tu sois en train de clarifier ton idée ou de structurer une activité existante. Les outils sont conçus pour s'adapter à ton niveau d'avancement."
    },
    {
      question: "Quelle est la différence entre VisionLab Essential et VisionLab Experience ?",
      answer: "VisionLab Essential t'offre les outils fondamentaux pour démarrer : audit de marque IA, générateur d'identité visuelle, 5 idées de contenu par mois et ta roadmap 30 jours. VisionLab Experience inclut tout cela, plus un accès illimité au contenu, le Content Studio complet, l'annuaire business, et un accompagnement stratégique approfondi."
    },
    {
      question: "Puis-je changer de formule ?",
      answer: "Oui. Tu peux passer de VisionLab Essential à VisionLab Experience. Le passage à Experience se fait en souscrivant à la nouvelle formule au tarif en vigueur."
    },
    {
      question: "Comment fonctionne la résiliation ?",
      answer: "Tu peux résilier ton abonnement à tout moment depuis ton espace. L'accès reste actif jusqu'à la fin de ta période en cours. Pas d'engagement, pas de frais cachés."
    }
  ];

  // Tools/Features
  const tools = [
    {
      icon: Compass,
      name: "VisionIdentity",
      description: "Clarifie ton identité de marque unique et ton positionnement."
    },
    {
      icon: Target,
      name: "BrandAudit",
      description: "Analyse complète de ta marque pour identifier tes forces et opportunités."
    },
    {
      icon: Palette,
      name: "ContentGenerator",
      description: "Crée du contenu aligné avec ta vision et ta stratégie."
    },
    {
      icon: Lightbulb,
      name: "VisionBoard",
      description: "Visualise et structure tes objectifs et ta direction."
    },
    {
      icon: Calendar,
      name: "InstagramStrategy",
      description: "Planifie ta présence avec une stratégie de contenu cohérente."
    },
    {
      icon: Sparkles,
      name: "Content Studio",
      description: "Ton espace créatif pour produire du contenu premium."
    }
  ];

  // Transformation steps
  const transformationSteps = [
    {
      icon: Compass,
      title: "Clarifie",
      description: "Ta vision, ton identité et ton positionnement"
    },
    {
      icon: BarChart3,
      title: "Structure",
      description: "Tes objectifs en plan d'action concret"
    },
    {
      icon: Sparkles,
      title: "Crée",
      description: "Ton contenu et ton univers de marque"
    },
    {
      icon: CheckCircle,
      title: "Exécute",
      description: "Et avance vers des résultats concrets"
    }
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#D4AF37] text-xl">✦</span>
            <span className="font-light tracking-[0.2em] text-sm">
              <span className="text-white">Vision</span>
              <span className="text-[#D4AF37]">Lab</span>
            </span>
            <span className="text-[#666] text-[10px] tracking-wider ml-1">BY CLIERXTER</span>
          </div>
          {/* Hide on mobile - sticky CTA handles mobile conversion */}
          <Button 
            onClick={handleCTA}
            className="hidden sm:flex bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium text-xs px-4 py-2 rounded-full"
            data-testid="header-cta"
          >
            Rejoins VisionLab
          </Button>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent z-10" />
          <img 
            src={HERO_IMAGE}
            alt="Entrepreneure VisionLab"
            className="w-full h-full object-cover object-center opacity-60"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <motion.div {...fadeIn}>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] rounded-full border border-[#D4AF37]/30 mb-6">
                <span className="text-[#D4AF37] text-xs">✦</span>
                <span className="text-[#A1A1AA] text-xs tracking-wider">TON BUSINESS COMPANION</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-6">
                Tu as la <span className="text-[#D4AF37]">vision</span>.<br />
                <span className="font-medium">Maintenant, exécute-la.</span>
              </h1>

              {/* Value Proposition */}
              <p className="text-lg sm:text-xl text-[#A1A1AA] leading-relaxed mb-8 max-w-xl">
                VisionLab est ton business companion pour transformer tes idées en stratégie, 
                ta stratégie en actions et ta vision en résultats.
              </p>

              {/* CTAs - Primary hidden on mobile (sticky handles it), shown on desktop */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={handleCTA}
                  className="hidden sm:flex bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-8 py-4 rounded-full text-base items-center justify-center gap-2 group"
                  data-testid="hero-cta-primary"
                >
                  Rejoins VisionLab
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  onClick={() => scrollToSection('discover')}
                  variant="outline"
                  className="border-[#333] hover:border-[#D4AF37] text-white hover:text-[#D4AF37] px-8 py-4 rounded-full text-base bg-transparent"
                  data-testid="hero-cta-secondary"
                >
                  Découvrir VisionLab
                </Button>
              </div>

              {/* Transformation keywords */}
              <div className="flex flex-wrap gap-6 text-[#666]">
                {['Clarté', 'Stratégie', 'Création', 'Action', 'Résultats'].map((word, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-[#D4AF37]">✦</span>
                    <span>{word}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
          <motion.div 
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-[#666] cursor-pointer"
            onClick={() => scrollToSection('problem')}
          >
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section id="problem" className="py-20 sm:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-8">
                <span className="text-[#F5F0E8]">Des idées plein la tête...</span><br />
                <span className="text-[#D4AF37] font-medium">Mais parfois difficile de tout structurer ?</span>
              </h2>
              
              <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mb-8" />
              
              <p className="text-[#A1A1AA] text-lg sm:text-xl leading-relaxed mb-6">
                Tu sais où tu veux aller. Mais entre les idées, la stratégie, le contenu 
                et tout ce qu'il faut gérer, il est facile de s'éparpiller.
              </p>
              
              <p className="text-white text-lg sm:text-xl leading-relaxed">
                <span className="text-[#D4AF37]">Tu n'es pas seule.</span><br />
                VisionLab rassemble au même endroit les outils et la structure dont tu as besoin 
                pour retrouver de la clarté et transformer ta vision en actions concrètes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRANSFORMATION SECTION */}
      <section id="discover" className="py-20 sm:py-32 bg-gradient-to-b from-[#0A0A0A] to-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4">
              Un parcours clair pour une <span className="text-[#D4AF37]">transformation réelle</span>.
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              VisionLab t'accompagne étape par étape pour transformer ta vision en résultats concrets.
            </p>
          </motion.div>

          {/* Transformation Path */}
          <div className="relative">
            {/* Connection line - desktop only */}
            <div className="hidden lg:block absolute top-1/2 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37] to-[#D4AF37]/20 -translate-y-1/2" />
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {transformationSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="relative text-center"
                >
                  <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-full border-2 border-[#D4AF37]/50 bg-[#0A0A0A] flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">{step.title}</h3>
                  <p className="text-[#A1A1AA] text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Button 
              onClick={handleCTA}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-8 py-4 rounded-full text-base"
              data-testid="transformation-cta"
            >
              Je passe à l'action
            </Button>
          </motion.div>
        </div>
      </section>

      {/* TOOLS/FEATURES SECTION */}
      <section className="py-20 sm:py-32 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4">
              Des outils puissants pour <span className="text-[#D4AF37]">avancer à chaque étape</span>.
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Tout ce dont tu as besoin, au même endroit.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 bg-[#1a1a1a] rounded-2xl border border-[#2a2a2a] hover:border-[#D4AF37]/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <tool.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{tool.name}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{tool.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-20 sm:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4">
              Comment <span className="text-[#D4AF37]">ça marche</span> ?
            </h2>
            <p className="text-[#A1A1AA] text-lg">
              Trois étapes simples pour commencer ta transformation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Rejoins VisionLab",
                description: "Choisis l'expérience qui correspond à ton business et accède immédiatement à ton espace."
              },
              {
                step: "02",
                title: "Construis ta vision",
                description: "Utilise tes outils pour clarifier ton identité, structurer ta stratégie et définir tes priorités."
              },
              {
                step: "03",
                title: "Passe à l'action",
                description: "Transforme progressivement ta vision en contenu, décisions et actions concrètes."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="text-6xl font-light text-[#D4AF37]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-medium text-white mb-3">{item.title}</h3>
                <p className="text-[#A1A1AA] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION - TEMPORARILY HIDDEN
          This section is hidden until we have real testimonials from external beta users.
          To re-enable: remove the {false && ...} wrapper below.
          
          Current status: 
          - Only 1 testimonial found (from founder Manuela)
          - No external beta testers with approved testimonials
          - Mechanisms exist: emotion_checkpoints, step_reviews, reviews collection
      */}
      {false && (
      <section className="py-20 sm:py-32 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#1a1a1a] mb-4">
              Ce qu'elles en disent
            </h2>
            <p className="text-[#666] text-lg max-w-2xl mx-auto">
              Retours de nos membres bêta.
            </p>
          </motion.div>

          {/* Real testimonial from beta with consent_testimonial: true */}
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl"
            >
              <div className="flex flex-col items-center text-center">
                <div className="text-6xl text-[#D4AF37]/30 mb-4">"</div>
                <p className="text-xl sm:text-2xl text-[#1a1a1a] leading-relaxed italic mb-6 max-w-2xl">
                  Je me sens encore plus inspirée après avoir pris du recul !
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#D4AF37]">✦</span>
                  <span className="text-[#666] text-sm tracking-wider uppercase">Étape Vision</span>
                </div>
                <div>
                  <p className="font-medium text-[#1a1a1a]">Manuela</p>
                  <p className="text-[#666] text-sm">Membre Beta • Avril 2026</p>
                </div>
              </div>
            </motion.div>

            {/* Note about more testimonials coming */}
            <p className="text-center text-[#999] text-sm mt-8 italic">
              Plus de témoignages à venir au fur et à mesure de l'ouverture de VisionLab.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* PRICING SECTION */}
      <section id="pricing" className="py-20 sm:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4">
              Choisis ton <span className="text-[#D4AF37]">expérience</span> VisionLab.
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Commence à construire ta vision dès aujourd'hui.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 p-1.5 bg-[#1a1a1a] rounded-full">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-[#A1A1AA] hover:text-white'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'annual' 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-[#A1A1AA] hover:text-white'
                }`}
              >
                Annuel
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingCycle === 'annual' ? 'bg-black/20' : 'bg-[#D4AF37]/20 text-[#D4AF37]'
                }`}>
                  -17%
                </span>
              </button>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          {!loading && plans && (
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {plans.map((plan, index) => {
                const price = billingCycle === 'annual' 
                  ? (isFoundingVisionary && plan.founding_visionary_price_annual 
                      ? plan.founding_visionary_price_annual 
                      : plan.price_annual)
                  : (isFoundingVisionary && plan.founding_visionary_price_monthly 
                      ? plan.founding_visionary_price_monthly 
                      : plan.price_monthly);
                
                const isExperience = plan.plan_id === 'experience';
                
                return (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className={`relative rounded-3xl p-8 ${
                      isExperience 
                        ? 'bg-gradient-to-b from-[#D4AF37]/20 to-[#1a1a1a] border-2 border-[#D4AF37]' 
                        : 'bg-[#1a1a1a] border border-[#2a2a2a]'
                    }`}
                  >
                    {isExperience && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#D4AF37] rounded-full">
                        <span className="text-black text-xs font-semibold tracking-wider">RECOMMANDÉ</span>
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-2xl font-medium text-white mb-2">{plan.name}</h3>
                      <p className="text-[#A1A1AA] text-sm">{plan.tagline}</p>
                    </div>

                    <div className="mb-8">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-light text-white">
                          {plan.currency} {price.toFixed(0)}
                        </span>
                        <span className="text-[#A1A1AA]">
                          /{billingCycle === 'annual' ? 'an' : 'mois'}
                        </span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-[#D4AF37] text-sm mt-1">
                          Soit {(price / 12).toFixed(0)} CHF/mois
                        </p>
                      )}
                      {isFoundingVisionary && (
                        <p className="text-[#D4AF37] text-sm mt-1">
                          ✨ Prix Founding Visionary (-20%)
                        </p>
                      )}
                    </div>

                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                          <span className="text-[#A1A1AA] text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSelectPlan(plan.plan_id)}
                      className={`w-full py-4 rounded-full font-semibold ${
                        isExperience
                          ? 'bg-[#D4AF37] hover:bg-[#C4A030] text-black'
                          : 'bg-white hover:bg-[#F5F0E8] text-black'
                      }`}
                      data-testid={`select-${plan.plan_id}`}
                    >
                      Choisir {plan.name}
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Reassurance */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-8 mt-12 text-[#666] text-sm"
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <span>Paiement sécurisé</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span>Résiliation à tout moment</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#D4AF37]" />
              <span>Sans engagement</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 sm:py-32 bg-[#0f0f0f]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-light mb-4">
              Questions <span className="text-[#D4AF37]">fréquentes</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#1a1a1a] rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-white">{item.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#666]" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-[#A1A1AA] leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section id="final-cta-section" className="relative py-20 sm:py-32">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70 z-10" />
          <img 
            src={FINAL_CTA_IMAGE}
            alt="Workspace VisionLab"
            className="w-full h-full object-cover opacity-40"
          />
        </div>

        <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6">
              Ta vision mérite plus que de rester une idée.<br />
              <span className="text-[#D4AF37] font-medium">Il est temps de la construire.</span>
            </h2>

            <Button 
              onClick={handleCTA}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-10 py-5 rounded-full text-lg mb-8"
              data-testid="final-cta"
            >
              REJOINS VISIONLAB
            </Button>

            <div className="flex flex-wrap justify-center gap-4 text-[#A1A1AA]">
              {['Clarté', 'Stratégie', 'Création', 'Action'].map((word, i) => (
                <React.Fragment key={i}>
                  <span>{word}</span>
                  {i < 3 && <span className="text-[#D4AF37]">•</span>}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER - with bottom padding for mobile sticky CTA */}
      <footer id="join-footer" className="py-12 pb-24 lg:pb-12 bg-[#0A0A0A] border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-[#D4AF37] text-xl">✦</span>
              <span className="font-light tracking-[0.2em] text-sm">
                <span className="text-white">Vision</span>
                <span className="text-[#D4AF37]">Lab</span>
              </span>
              <span className="text-[#666] text-[10px] tracking-wider ml-1">BY CLIERXTER</span>
            </div>
            
            <div className="flex gap-6 text-[#666] text-sm">
              <a href="/legal" className="hover:text-[#D4AF37] transition-colors">Mentions légales</a>
              <a href="/privacy" className="hover:text-[#D4AF37] transition-colors">Confidentialité</a>
              <a href="/terms" className="hover:text-[#D4AF37] transition-colors">CGV</a>
            </div>

            <p className="text-[#666] text-sm">
              © {new Date().getFullYear()} Clierxter. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* STICKY CTA - Mobile (hidden when footer/final CTA visible) */}
      {showStickyCta && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent lg:hidden">
          <Button 
            onClick={handleCTA}
            className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold py-4 rounded-full shadow-lg"
            data-testid="sticky-cta"
          >
            Rejoins VisionLab
          </Button>
        </div>
      )}
    </div>
  );
};

export default Join;
